import type { RunRow } from "../api/runs";
import { setupSteps, type Setup } from "../api/sources";
import { Chip, Empty } from "./chrome";
import { connectHref, type IntegrationsStep } from "./integrationsStep";

/**
 * Whether the run list stands before the workspace's first investigation, and so carries
 * the checklist. A test alert's run is never the first investigation (#985): a list of
 * tests alone defers to the setup read, which is the whole workspace's and not one page's,
 * and an unread setup is not guessed at.
 */
export function beforeFirstInvestigation(runs: Array<Pick<RunRow, "test">>, setup: Setup | null): boolean {
  if (runs.some((run) => run.test !== true)) return false;
  if (runs.length === 0) return true;
  return setup !== null && setup.first_run_id === null;
}

/**
 * The empty list as a checklist (#422): what is left before the first investigation,
 * each step a link to the screen that does it. The list re-reads while it stays
 * empty, so the first alert's run replaces this without a reload.
 */
export function FirstRun({
  setup,
  integrations = null,
}: {
  setup: Setup | null;
  /** The optional step (#602), or null when there is none to show. Never one of `steps`. */
  integrations?: IntegrationsStep | null;
}) {
  if (setup === null) {
    return (
      <Empty title="No investigations yet" mark="○">
        <p>A forwarded alert opens the first one.</p>
      </Empty>
    );
  }
  const steps = setupSteps(setup);
  const left = steps.filter((step) => !step.done);
  return (
    <div className="panel__body stack setup-first-run" data-testid="first-run">
      {left.length === 0 ? (
        <Empty title="Setup saved. Waiting for the first alert." mark="◔">
          <p>
            The next alarm that reaches your alert source opens the first investigation, and it
            appears here when the list refreshes.
          </p>
          <a className="btn" href="/sources">Review alert sources</a>
        </Empty>
      ) : (
        <>
          <div>
            <p className="eyebrow">Your first investigation</p>
            <h3>Before the first investigation</h3>
            <p className="muted">
              Connect your account, choose an alert source and bring your model provider.
            </p>
          </div>
          <ol className="setup-checklist" aria-label="Setup steps">
            {steps.map((step, index) => (
              <li key={step.href} className="row row--between row--wrap">
                <span className="setup-checklist__number" aria-hidden="true">{index + 1}</span>
                <span className="grow">
                  <Chip tone={step.done ? "good" : undefined}>{step.done ? "done" : "to do"}</Chip>{" "}
                  {step.label}
                </span>
                {step.done ? null : (
                  <a className="btn btn--small" href={step.href} aria-label={step.label}>
                    Continue
                  </a>
                )}
              </li>
            ))}
          </ol>
        </>
      )}
      {integrations === null ? null : <IntegrationsRow connected={integrations} />}
    </div>
  );
}

const SERVICES = [
  { kind: "jira", name: "Jira" },
  { kind: "slack", name: "Slack" },
] as const;

/**
 * Under the list and inside the waiting state alike (#602): unnumbered, and outside
 * the `<ol>`, because it is not a step toward the first investigation.
 */
function IntegrationsRow({ connected }: { connected: IntegrationsStep }) {
  const any = SERVICES.some((service) => connected[service.kind]);
  return (
    <div className="setup-optional row row--between row--wrap" data-testid="first-run-integrations">
      <span className="grow">
        <Chip tone={any ? "good" : undefined}>{any ? "connected" : "optional"}</Chip>{" "}
        Send results to Jira or Slack
      </span>
      <span className="actions">
        {SERVICES.map((service) =>
          connected[service.kind] ? (
            <span key={service.kind} className="muted">{service.name} connected</span>
          ) : (
            <a key={service.kind} className="btn btn--small" href={connectHref(service.kind)}>
              Connect {service.name}
            </a>
          ),
        )}
      </span>
    </div>
  );
}
